import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useAppSelector } from './store';
import { isBarberLoggedIn, isCustomerLoggedIn, getUserType } from '../slices/authSlice';

/**
 * Decodes a JWT token to extract payload data
 */
const decodeJwt = (token: string): Record<string, unknown> | null => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
};

/**
 * Hook that sets Sentry user context based on authentication state
 */
export const useSentryContext = () => {
    const isBarber = useAppSelector(isBarberLoggedIn);
    const isCustomer = useAppSelector(isCustomerLoggedIn);
    const userType = useAppSelector(getUserType);
    const barberToken = useAppSelector((state) => state.auth.token);
    const customerToken = useAppSelector((state) => state.auth.customerToken);

    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            return; // Sentry is not initialized in development
        }

        if (isBarber && barberToken) {
            const decoded = decodeJwt(barberToken);
            if (decoded) {
                Sentry.setUser({
                    email: decoded.username as string || decoded.email as string,
                    id: decoded.sub as string || decoded.id as string,
                });
                Sentry.setTag('user_type', 'barber');
                Sentry.setContext('auth', {
                    userType: 'barber',
                    tokenExp: decoded.exp,
                    tokenIat: decoded.iat,
                });
            }
        } else if (isCustomer && customerToken) {
            const decoded = decodeJwt(customerToken);
            if (decoded) {
                Sentry.setUser({
                    email: decoded.username as string || decoded.email as string,
                    id: decoded.sub as string || decoded.id as string,
                });
                Sentry.setTag('user_type', 'customer');
                Sentry.setContext('auth', {
                    userType: 'customer',
                    tokenExp: decoded.exp,
                    tokenIat: decoded.iat,
                });
            }
        } else {
            // Clear user context when logged out
            Sentry.setUser(null);
            Sentry.setTag('user_type', null);
            Sentry.setContext('auth', null);
        }
    }, [isBarber, isCustomer, userType, barberToken, customerToken]);
};
